/**
 * Spherical geometry helpers. Everything works in [lng, lat] degrees and
 * returns metres / degrees-from-north, matching the GeoJSON + Mapbox convention.
 *
 * Distances use the equirectangular approximation scaled by cos(lat) rather than
 * full haversine. Over the segment lengths we deal with (tens of metres between
 * route vertices) the error is far below GPS noise, and the navigator calls
 * these on every fix so the cheaper form is worth it.
 */

import type { LngLat } from './types';

const R = 6371008.8; // IUGG mean earth radius, metres
const DEG = Math.PI / 180;

export function toRad(deg: number): number {
  return deg * DEG;
}

export function toDeg(rad: number): number {
  return rad / DEG;
}

/** Great-circle distance in metres. Accurate everywhere; use for long spans. */
export function haversine(a: LngLat, b: LngLat): number {
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Fast planar distance in metres, valid for short spans. */
export function distance(a: LngLat, b: LngLat): number {
  const mLat = (a[1] + b[1]) / 2;
  const x = toRad(b[0] - a[0]) * Math.cos(toRad(mLat));
  const y = toRad(b[1] - a[1]);
  return Math.sqrt(x * x + y * y) * R;
}

/** Initial bearing from a to b, degrees clockwise from true north, 0..360. */
export function bearing(a: LngLat, b: LngLat): number {
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const dLng = toRad(b[0] - a[0]);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/** Smallest signed difference b - a, in (-180, 180]. */
export function bearingDelta(a: number, b: number): number {
  let d = ((b - a + 540) % 360) - 180;
  if (d === -180) d = 180;
  return d;
}

/** Interpolate towards `to` the short way around the compass. */
export function lerpBearing(from: number, to: number, t: number): number {
  return (from + bearingDelta(from, to) * t + 360) % 360;
}

export function destinationPoint(from: LngLat, bearingDeg: number, metres: number): LngLat {
  const d = metres / R;
  const br = toRad(bearingDeg);
  const lat1 = toRad(from[1]);
  const lng1 = toRad(from[0]);
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(br));
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(br) * Math.sin(d) * Math.cos(lat1),
      Math.cos(d) - Math.sin(lat1) * Math.sin(lat2),
    );
  return [toDeg(lng2), toDeg(lat2)];
}

export interface NearestOnSegment {
  point: LngLat;
  /** metres from p to the segment */
  distance: number;
  /** 0..1 position of the projection along a->b */
  t: number;
}

/**
 * Perpendicular projection of p onto segment a->b, clamped to the endpoints.
 * Works in a local metre-space tangent plane so the projection stays correct
 * at Thai latitudes where a degree of longitude is ~0.97 of a degree of latitude.
 */
export function nearestPointOnSegment(p: LngLat, a: LngLat, b: LngLat): NearestOnSegment {
  const kx = Math.cos(toRad((a[1] + b[1]) / 2));
  const ax = a[0] * kx;
  const bx = b[0] * kx;
  const px = p[0] * kx;

  const dx = bx - ax;
  const dy = b[1] - a[1];
  const lenSq = dx * dx + dy * dy;

  let t = 0;
  if (lenSq > 0) {
    t = ((px - ax) * dx + (p[1] - a[1]) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
  }

  const point: LngLat = [a[0] + (b[0] - a[0]) * t, a[1] + dy * t];
  return { point, distance: distance(p, point), t };
}

/** Nearest projection onto a whole line, including its distance along. */
export function projectPointOntoLine(
  point: LngLat,
  line: LngLat[],
): { point: LngLat; distance: number; along: number } | null {
  if (line.length < 2) return null;
  const cumulative = cumulativeDistances(line);
  let best: { point: LngLat; distance: number; along: number } | null = null;
  for (let i = 0; i < line.length - 1; i++) {
    const projected = nearestPointOnSegment(point, line[i], line[i + 1]);
    if (best && projected.distance >= best.distance) continue;
    const segmentLength = cumulative[i + 1] - cumulative[i];
    best = {
      point: projected.point,
      distance: projected.distance,
      along: cumulative[i] + segmentLength * projected.t,
    };
  }
  return best;
}

/** Cumulative distance along a line, in metres. `out[i]` is distance to vertex i. */
export function cumulativeDistances(line: LngLat[]): number[] {
  const out = new Array<number>(line.length);
  out[0] = 0;
  for (let i = 1; i < line.length; i++) {
    out[i] = out[i - 1] + distance(line[i - 1], line[i]);
  }
  return out;
}

/** Total length of a line in metres. */
export function lineLength(line: LngLat[]): number {
  let total = 0;
  for (let i = 1; i < line.length; i++) total += distance(line[i - 1], line[i]);
  return total;
}

/** Point at `metres` along a line, plus the bearing of the segment it lands on. */
export function pointAlongLine(
  line: LngLat[],
  metres: number,
): { point: LngLat; bearing: number; index: number } {
  if (line.length === 0) return { point: [0, 0], bearing: 0, index: 0 };
  if (line.length === 1 || metres <= 0) {
    return { point: line[0], bearing: line.length > 1 ? bearing(line[0], line[1]) : 0, index: 0 };
  }
  let travelled = 0;
  for (let i = 1; i < line.length; i++) {
    const seg = distance(line[i - 1], line[i]);
    if (travelled + seg >= metres) {
      const t = seg === 0 ? 0 : (metres - travelled) / seg;
      const point: LngLat = [
        line[i - 1][0] + (line[i][0] - line[i - 1][0]) * t,
        line[i - 1][1] + (line[i][1] - line[i - 1][1]) * t,
      ];
      return { point, bearing: bearing(line[i - 1], line[i]), index: i - 1 };
    }
    travelled += seg;
  }
  const n = line.length;
  return { point: line[n - 1], bearing: bearing(line[n - 2], line[n - 1]), index: n - 2 };
}

/** Bounding box [w, s, e, n] of a set of points. */
export function bounds(points: LngLat[]): [number, number, number, number] {
  let w = 180;
  let s = 90;
  let e = -180;
  let n = -90;
  for (const [lng, lat] of points) {
    if (lng < w) w = lng;
    if (lng > e) e = lng;
    if (lat < s) s = lat;
    if (lat > n) n = lat;
  }
  return [w, s, e, n];
}
